/**
 * Be aware of this imports sorts, L from leaflet must came after React
 * but before all other leaflet packages references (imports)
 */
/* eslint sort-imports:["off"] */
import { useCallback, useEffect, useRef, useState } from "react";

import { saveAs } from "file-saver";
import L from "leaflet";

import "leaflet-contextmenu";
import "leaflet-contextmenu/dist/leaflet.contextmenu.css";
import "leaflet-measure/dist/leaflet-measure.css";
import "leaflet-measure/dist/leaflet-measure.pt_BR";
import "leaflet-routing-machine/";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";
import "leaflet-routing-machine/src/localization";
import "leaflet.fullscreen";
import "leaflet.vectorgrid/";
import "leaflet/dist/leaflet.css";

import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";

import "./L.TileLayer.BetterWMS";
import "./Leaflet.Icon.Glyph";

import { geoserverClient } from "../lib/clients";
import {
  alreadyVisitedPointsUpdatedListener,
  loadingPrintListener,
  reportPrintedListener,
} from "../listeners";
import { useAppConfigContext } from "../providers/hooks/AppConfigContext";
import { useFiltersData } from "../providers/hooks/FiltersContext";
import { useGeoServerConfig } from "../providers/hooks/GeoserverConfigContext";
import { useSetMap } from "../providers/hooks/MapContext";
import { useSelectedMapLayerContext } from "../providers/hooks/MapLayerSelectionContext";
import DetailsService from "../services/DetailsService";
import LoginService from "../services/LoginService";
import ProductsAPI from "../services/ProductsAPI";
import { LoggerFactory } from "../utils/loggerFactory";
import DataByAreaControl from "./controls/DataByAreaControl";
import FullScreenControl from "./controls/FullScreenControl";
import GoToLocationControl from "./controls/GoToLocationControl";
import MeasureControl from "./controls/MeasureControl";
import ScaleControl from "./controls/ScaleControl";

const log = LoggerFactory("map.js");

const VISITED_ICON_SIZE = 16;

const alreadyVisitedIcon = {
  great: () =>
    L.icon({
      iconUrl: "images/blue_check.png",
      iconSize: [VISITED_ICON_SIZE, VISITED_ICON_SIZE],
      iconAnchor: [VISITED_ICON_SIZE / 2, VISITED_ICON_SIZE / 2],
    }),
  mean: () =>
    L.icon({
      iconUrl: "images/yellow_check.png",
      iconSize: [VISITED_ICON_SIZE, VISITED_ICON_SIZE],
      iconAnchor: [VISITED_ICON_SIZE / 2, VISITED_ICON_SIZE / 2],
    }),
  small: () =>
    L.icon({
      iconUrl: "images/red_check.png",
      iconSize: [VISITED_ICON_SIZE, VISITED_ICON_SIZE],
      iconAnchor: [VISITED_ICON_SIZE / 2, VISITED_ICON_SIZE / 2],
    }),
};

const alreadyVisitedMarkers = [];

const Map = () => {
  const appConfig = useAppConfigContext();
  const [map, setMap] = useState(null);
  const mapContainer = useRef();
  const [layer, setLayer] = useState(null);
  const geoserverConfig = useGeoServerConfig();
  const [mapReady, setMapReady] = useState(false);
  const [showAlreadyVisitedPoints, setShowAlreadyVisitedPoints] =
    useState(true);
  const [center, setCenter] = useState(null);
  const [perimetersOverlay, setPerimetersOverlay] = useState(null);
  const [pointsOverlay, setPointsOverlay] = useState(null);
  const [polygonsOverlay, setPolygonsOverlay] = useState(null);
  const overlayLayers = useRef(null);
  const selectedMapLayer = useSelectedMapLayerContext();
  const setMapContext = useSetMap();
  const filters = useFiltersData();

  const fetchAndMarkAlreadyVisitedPoints = useCallback(() => {
    if (center && filters) {
      if (window.alreadyVisitedRequest) {
        clearTimeout(window.alreadyVisitedRequest);
      }
      if (showAlreadyVisitedPoints && map) {
        window.alreadyVisitedRequest = setTimeout(() => {
          const defaultBoundPoint = () => {
            return { lat: 0, lng: 0 };
          };
          const bounds = map.getBounds() ?? {
            getNorthWest: defaultBoundPoint,
            getNorthEast: defaultBoundPoint,
            getSouthEast: defaultBoundPoint,
            getSouthWest: defaultBoundPoint,
          };
          const coordinates = [
            bounds.getNorthWest(),
            bounds.getNorthEast(),
            bounds.getSouthEast(),
            bounds.getSouthWest(),
            bounds.getNorthWest(),
          ].map((boundPoint) => [boundPoint.lng, boundPoint.lat]);

          (async () => {
            while (alreadyVisitedMarkers.length > 0) {
              const mark = alreadyVisitedMarkers.pop();
              mark.remove();
            }
            const response = await DetailsService.findAlreadyVisitedPoints(
              coordinates,
              filters.toString(),
              filters,
            );
            if (Array.isArray(response)) {
              response.forEach((point) => {
                const newIcon = alreadyVisitedIcon[point.size.toLowerCase()]();
                const place = [point.lat, point.lng];
                const newMarker = L.marker(place, {
                  icon: newIcon,
                  bubblingMouseEvents: true,
                }).addTo(map);
                alreadyVisitedMarkers.push(newMarker);
              });
            }
          })();
        }, 1000);
      }
    }
  }, [center, filters, showAlreadyVisitedPoints, map]);

  alreadyVisitedPointsUpdatedListener.subscribe("map", ({ location }) => {
    fetchAndMarkAlreadyVisitedPoints(location);
  });

  const printReport = (reportData) => {
    return ProductsAPI.generateReport(reportData)
      .then((file) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onloadend = () => {
          const base64Data = fileReader.result;
          reportData.content = base64Data;
          window.recentlyPrintedReport = reportData;
        };

        saveAs(file, reportData.filename);
      })
      .then(() => {
        loadingPrintListener.publish(false);
        reportPrintedListener.publish();
      })
      .catch((err) => {
        log("printReport").error("{}", err.message ?? err);
        loadingPrintListener.publish(false);
      });
  };

  window.printDetails = async (propertiesToPrint) => {
    loadingPrintListener.publish(true);

    const reportData = JSON.parse(
      Buffer.from(propertiesToPrint, "base64").toString("utf8"),
    );

    return DetailsService.find(window.selectedAnnotationLocation)
      .then((response) => {
        const table = {
          "Empresa:": response.company,
          "App:": response.appName,
          "Já visitou:": response.alreadyVisited ? "Sim" : "Não",
          ...reportData,
        };
        for (const data of response.annotations) {
          table[data.name] = data.value;
        }

        return table;
      })
      .then((content) => {
        const report = {
          subject: "Relatório de Detalhes",
          content,
          filename: `Relatório de Detalhes ${new Date().toLocaleDateString(
            "pt-BR",
            {
              month: "short",
              day: "numeric",
              year: "numeric",
            },
          )}.pdf`.replaceAll(" ", "_"),
        };
        return printReport(report);
      })
      .catch((err) => {
        log("printDetails").error("{}", err.message ?? err);
        loadingPrintListener.publish(false);
      })
      .finally(() => {
        log("printDetails").info("finished");
        loadingPrintListener.publish(false);
      });
  };

  window.printMeasures = async () => {
    loadingPrintListener.publish(true);

    const popupData = document.getElementsByClassName(
      "leaflet-popup-content",
    )[0];

    const data = popupData.children[0].children;
    const area = data[1].innerText;
    const perimeter = data[2].innerText;

    const content = {
      Área: area,
      Perímetro: perimeter,
    };

    const report = {
      subject: "Relatório de Cálculo de Área",
      content,
      filename: `Relatório de Cálculo de Área ${new Date().toLocaleDateString(
        "pt-BR",
        {
          month: "short",
          day: "numeric",
          year: "numeric",
        },
      )}.pdf`.replaceAll(" ", "_"),
    };

    return printReport(report)
      .catch((err) => {
        log("printMeasures").error("{}", err.message ?? err);
        loadingPrintListener.publish(false);
      })
      .finally(() => {
        log("printMeasures").info("finished");
        loadingPrintListener.publish(false);
      });
  };

  window.printDataCalc = () => {
    loadingPrintListener.publish(true);

    const data = Array.from(
      document.getElementsByClassName("leaflet-popup-content")[0].children[0]
        .children[0].children,
    ).map((tr) => Array.from(tr.children).map((td) => td.innerText));

    const content = {};
    for (const item of data) {
      content[item[0]] = item?.[1] ?? "";
    }

    const report = {
      subject: "Relatório de Cálculo de Produtos Selecionados",
      content,
      filename:
        `Relatório de Cálculo de Produtos Selecionados ${new Date().toLocaleDateString(
          "pt-BR",
          {
            month: "short",
            day: "numeric",
            year: "numeric",
          },
        )}.pdf`.replaceAll(" ", "_"),
    };

    return printReport(report)
      .catch((err) => {
        log("printDataCalc").error("{}", err.message ?? err);
        loadingPrintListener.publish(false);
      })
      .finally(() => {
        log("printDataCalc").info("finished");
        loadingPrintListener.publish(false);
      });
  };

  // Define geoserver layers filters
  useEffect(() => {
    if (filters && pointsOverlay && geoserverConfig) {
      pointsOverlay.setParams({
        CQL_FILTER: geoserverConfig.pointsLayer
          .map(() => filters.toString())
          .join(";"),
      });
    }
  }, [filters, pointsOverlay, geoserverConfig]);

  // Request to render already visited points
  useEffect(() => {
    if (showAlreadyVisitedPoints) {
      alreadyVisitedPointsUpdatedListener.publish({ location: "layer" });
    } else {
      while (alreadyVisitedMarkers.length > 0) {
        const mark = alreadyVisitedMarkers.pop();
        mark.remove();
      }
    }
  }, [showAlreadyVisitedPoints]);

  // Create map
  useEffect(() => {
    if (!map) {
      const _map = L.map(mapContainer.current, { contextmenu: true }).on(
        "load",
        () => {
          setMapReady(true);
          alreadyVisitedPointsUpdatedListener.publish({ location: "load" });
        },
      );
      setMap(_map);
    }
  }, [map]);

  // Define map events listeners
  useEffect(() => {
    if (map) {
      map.on("moveend", (_) =>
        alreadyVisitedPointsUpdatedListener.publish({ location: "move" }),
      );

      map.on("popupopen", (event) => {
        map.setView(event.target._popup._latlng, map.getZoom(), {
          animate: true,
          duration: 1,
        });
      });
    }
  }, [map]);

  // When map is ready, get the map center for first view
  useEffect(() => {
    if (geoserverConfig?.center && !center) {
      setCenter(geoserverConfig.center);
    }
  }, [center, geoserverConfig]);

  // Define map view one time and try fetch already visited points
  useEffect(() => {
    if (center && map) {
      map.setView(center, 12);
      setShowAlreadyVisitedPoints(true);
      setMapContext(map);
    }
  }, [center, map, setMapContext]);

  // Define the map base layers
  useEffect(() => {
    if (appConfig?.mapboxToken && mapReady && !layer) {
      const streetLayer = L.tileLayer(
        `https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=${
          appConfig.mapboxToken ?? ""
        }`,
        {
          maxZoom: 20,
          id: "mapbox/streets-v11",
          tileSize: 512,
          zoomOffset: -1,
        },
      ).addTo(map);

      const googleSat = L.tileLayer(
        "https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
        {
          maxZoom: 20,
          subdomains: ["mt0", "mt1", "mt2", "mt3"],
        },
      );

      setLayer({ default: streetLayer, satellite: googleSat });
    }
  }, [appConfig, map, mapReady, layer]);

  // Refresh custom perimeters layer overlay
  useEffect(() => {
    if (center) {
      setPerimetersOverlay((current) => {
        if (current) {
          current.remove();
        }

        const layer = L.tileLayer
          .wms(geoserverClient, {
            layers: geoserverConfig.perimetersLayer.join(","),
            transparent: true,
            format: "image/png",
          })
          .setZIndex(100);

        return layer;
      });
    }
  }, [center, geoserverConfig, map]);

  // Refresh custom points layer overlay
  useEffect(() => {
    if (center) {
      setPointsOverlay((current) => {
        if (current) {
          current.remove();
        }

        const layer = L.tileLayer
          .betterWms(geoserverClient, {
            layers: geoserverConfig.pointsLayer.join(","),
            transparent: true,
            format: "image/png",
          })
          .setZIndex(400);

        layer.on("remove", () => {
          setShowAlreadyVisitedPoints(false);
        });

        layer.on("add", () => {
          setShowAlreadyVisitedPoints(true);
        });

        return layer;
      });
    }
  }, [center, geoserverConfig, map]);

  // Refresh custom polygons layer overlay
  useEffect(() => {
    if (center) {
      setPolygonsOverlay((current) => {
        if (current) {
          current.remove();
        }

        const layer = L.tileLayer
          .wms(geoserverClient, {
            layers: geoserverConfig.polygonsLayer.join(","),
            transparent: true,
            format: "image/png",
          })
          .setZIndex(300);

        return layer;
      });
    }
  }, [center, geoserverConfig, map]);

  // Add custom overlays to map and refresh overlays component
  useEffect(() => {
    if (perimetersOverlay && pointsOverlay && polygonsOverlay) {
      const customOverlays = {
        '<img src="images/Municpios.png" /> Municípios': perimetersOverlay,
        [appConfig.overlayTitle]: polygonsOverlay,
        [appConfig.overlay]: pointsOverlay,
      };

      const layers = L.control
        .layers({}, customOverlays, { collapsed: false })
        .addTo(map);

      overlayLayers.current?.remove();
      overlayLayers.current = layers;

      for (const layer of Object.values(customOverlays)) {
        layer.addTo(map);
      }
    }
  }, [appConfig, perimetersOverlay, pointsOverlay, polygonsOverlay, map]);

  useEffect(() => {
    if (layer && map) {
      const layerToEnable = layer[selectedMapLayer];
      const layersToRemoveFromMap = Object.entries(layer)
        .filter(([k, _v]) => k !== selectedMapLayer)
        .map(([_k, v]) => v);

      layerToEnable.addTo(map);
      layersToRemoveFromMap.forEach((layerOut) => layerOut.remove());
    }
  }, [selectedMapLayer, layer, map]);

  const handleLogin = useCallback(() => {
    (async () => {
      LoginService.login();
    })();
  }, []);

  const handleLogout = useCallback(() => {
    (async () => {
      LoginService.logout();
    })();
  }, []);

  const renderLoginLogoutButton = () => {
    if (LoginService.getMode() === "logged") {
      return (
        <button id="logout" onClick={handleLogout}>
          Sair(Logout)
        </button>
      );
    }

    return (
      <button id="login" onClick={handleLogin}>
        Entrar(Login)
      </button>
    );
  };

  return (
    <div
      id="map"
      style={{ padding: 0, margin: 0, width: "100%", height: "100vh" }}
      ref={(el) => (mapContainer.current = el)}
    >
      {renderLoginLogoutButton()}
      <button id="toggleMenuButton" type="button" className="toggleMenu">
        |||
      </button>
      <FullScreenControl />
      <MeasureControl />
      <DataByAreaControl />
      <ScaleControl />
      <GoToLocationControl />
    </div>
  );
};

export default Map;
